import { Modal, Form, Input, Button, Space, Select } from 'antd';
import { useSuperAdmin } from '../../../contexts/SuperAdminContext';
import { useEffect, useState } from 'react';

type WalletType = 'mercadopago' | 'telepagos';

interface WalletFormValues {
  walletType: WalletType;
  name: string;
  // MercadoPago fields
  mpUserId?: string;
  token?: string;
  // TelePagos fields
  username?: string;
  password?: string;
  description?: string;
  userId: string; // Changed from adminId to userId to match Panel pattern
}

interface WalletModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: {
    id: string;
    name: string;
    mpUserId?: string;
    token?: string;
    username?: string;
    password?: string;
    description?: string;
    userId: string;
    adminId?: string; // For pre-selecting admin when creating from Entity Management
  };
}

export const WalletModal = ({ open, onClose, mode, initialData }: WalletModalProps) => {
  const [form] = Form.useForm<WalletFormValues>();
  const { users, createWallet, editWallet } = useSuperAdmin();
  const [walletType, setWalletType] = useState<WalletType>('mercadopago');

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      // Detectar tipo de wallet basado en campos presentes
      const detectedType: WalletType = initialData.mpUserId ? 'mercadopago' : 'telepagos';
      setWalletType(detectedType);

      form.setFieldsValue({
        walletType: detectedType,
        name: initialData.name,
        mpUserId: initialData.mpUserId,
        token: initialData.token,
        username: initialData.username,
        password: initialData.password,
        description: initialData.description,
        userId: initialData.userId
      });
    } else {
      form.resetFields();
      setWalletType('mercadopago'); // Default to MercadoPago for new wallets
      form.setFieldsValue({
        walletType: 'mercadopago'
      });

      // Pre-seleccionar admin específico cuando se crea desde Entity Management
      if (mode === 'create' && initialData?.adminId) {
        form.setFieldsValue({
          userId: initialData.adminId
        });
      }
    }
  }, [form, initialData, mode, open]);

const handleSubmit = (values: WalletFormValues) => {
  if (mode === 'edit' && initialData) {
    editWallet({
      id: initialData.id,
      data: values  // Since values already contains userId
    });
  } else {
    // Log the values to verify userId is present
    
    createWallet(values); // Direct pass since structure matches CreateWalletDTO
  }
  onClose();
};
  const eligibleAdmins = users.filter(user => 
    ['ADMIN', 'DISTRIBUITOR'].includes(user.role)
  );

  return (
    <Modal
      title={`${mode === 'edit' ? 'Edit' : 'Create'} Wallet`}
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={null}
      maskClosable={false}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Wallet Type"
          name="walletType"
          rules={[{ required: true, message: 'Please select wallet type' }]}
        >
          <Select
            onChange={(value: WalletType) => setWalletType(value)}
            disabled={mode === 'edit'} // No permitir cambiar tipo en edición
          >
            <Select.Option value="mercadopago">Mercado Pago</Select.Option>
            <Select.Option value="telepagos">TelePagos</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please input name' }]}
        >
          <Input />
        </Form.Item>

        {walletType === 'mercadopago' ? (
          <>
            <Form.Item
              label="Mercado Pago User ID"
              name="mpUserId"
              rules={[{ required: true, message: 'Please input MP user ID' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Token"
              name="token"
              rules={[{ required: true, message: 'Please input token' }]}
            >
              <Input.Password />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: 'Please input username' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Please input password' }]}
            >
              <Input.Password />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Description"
          name="description"
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Admin"
          name="userId" // Changed from adminId to userId
          rules={[{ required: true, message: 'Please select admin' }]}
        >
          <Select>
            {eligibleAdmins.map(user => (
              <Select.Option key={user.id} value={user.id}>
                {user.username} ({user.role})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={() => {
              form.resetFields();
              onClose();
            }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};